/* eslint-disable @atlaskit/design-system/no-unsafe-design-token-usage */
import React, { type SyntheticEvent, type ReactNode, memo, useCallback } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/restricted/use-debounce
import { useDebouncedCallback } from 'use-debounce';
import Textfield, { type TextFieldProps } from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { fg } from '@atlassian/jira-feature-gating';
import { type MessageDescriptorV2 as MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { SearchField } from '@atlassian/jira-searchfield';
import { getTextFieldAttributes } from '../../common/utils/text-field-attributes';
import { useSimpleSearch } from '../../controllers/simple-search';

type SimpleSearchFieldProps = {
	placeholder: MessageDescriptor;
	view: string;
	describedbyId?: string;
};

type SimpleSearchStatelessFieldProps = {
	autoFocus?: boolean;
	inputRef?: React.Ref<HTMLElement | null>;
	placeholder: MessageDescriptor;
	view: string;
	initialValue?: string | undefined;
	onChange: (arg1: string) => void;
	onFocus?: (arg1: SyntheticEvent<HTMLElement>) => void;
	shouldFitContainerWidth?: boolean;
	themedField?: boolean;
	isDisabled?: boolean;
	testId?: string;
	// Make label prop mandatory when cleaning up the feature flag jwm_header_filters_a11y_fixes
	label?: string;
	describedbyId?: string;
};

export const SimpleSearchStatelessField = memo<SimpleSearchStatelessFieldProps>(
	({
		autoFocus = false,
		inputRef,
		placeholder,
		initialValue,
		onChange,
		onFocus,
		themedField = true,
		shouldFitContainerWidth = false,
		view,
		isDisabled,
		testId,
		label,
		describedbyId,
	}: SimpleSearchStatelessFieldProps) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		// Clean up with fg jwm-a11y-audit-fixes
		const handleChange = useCallback(
			(newValue: string) => {
				let eventAttributes = {};
				if (ff('jwm-attachments-m2')) {
					eventAttributes = getTextFieldAttributes(newValue);
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'simpleSearchField updated', {
					view,
					...eventAttributes,
				});
				onChange(newValue);
			},
			[createAnalyticsEvent, onChange, view],
		);

		const [setOnSearchChangeDebounce] = useDebouncedCallback(handleChange, 500);

		const renderCustomSearchField = useCallback(
			({
				textFieldProps,
				searchIcon,
			}: {
				textFieldProps: TextFieldProps;
				searchIcon: ReactNode;
			}) => {
				const textField = (
					<Textfield
						testId={testId}
						{...textFieldProps}
						elemAfterInput={<RightIconWrapper>{searchIcon}</RightIconWrapper>}
					/>
				);
				return themedField ? <ThemedContainer>{textField}</ThemedContainer> : textField;
			},
			[testId, themedField],
		);

		const themedIconPrimaryColor = isDisabled
			? Tokens.COLOR_TEXT_DISABLED
			: Tokens.COLOR_TEXT_SUBTLEST;

		return (
			<SearchField
				// @ts-expect-error Type 'Ref<HTMLElement | null> | undefined' is not assignable to type '{ current: HTMLInputElement | null; } | undefined'. Type 'null' is not assignable to type '{ current: HTMLInputElement | null; } | undefined'
				inputRef={inputRef}
				autoFocus={autoFocus}
				isCompact
				shouldFitContainerWidth={shouldFitContainerWidth}
				isAlwaysExpanded
				isAutocompleteDisabled
				value={initialValue}
				placeholder={formatMessage(placeholder)}
				placeholderAlwaysVisible
				onFocus={onFocus}
				describedById={describedbyId}
				onChange={setOnSearchChangeDebounce}
				renderCustomSearchField={renderCustomSearchField}
				searchIconPrimaryColor={themedField ? themedIconPrimaryColor : undefined}
				isDisabled={isDisabled}
				{...(fg('jwm_header_filters_a11y_fixes') ? { ariaLabelText: label } : {})}
			/>
		);
	},
);

export const SimpleSearchField = memo<SimpleSearchFieldProps>(
	({ placeholder, view, describedbyId }: SimpleSearchFieldProps) => {
		const [simpleSearch, setSimpleSearch] = useSimpleSearch();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleFocus = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), 'simpleSearchField focused', {
				view,
			});
		}, [createAnalyticsEvent, view]);

		return (
			<Container>
				<SimpleSearchStatelessField
					view={view}
					onFocus={handleFocus}
					describedbyId={describedbyId}
					onChange={setSimpleSearch}
					initialValue={simpleSearch}
					placeholder={placeholder}
				/>
			</Container>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ThemedContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-background-disabled': Tokens.COLOR_BACKGROUND_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-background-input': Tokens.COLOR_BACKGROUND_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-background-input-pressed': Tokens.COLOR_BACKGROUND_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-background-input-hovered': Tokens.COLOR_BACKGROUND_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-border-input': Tokens.COLOR_BORDER_INPUT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-icon': Tokens.COLOR_TEXT_SUBTLE,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text': Tokens.COLOR_TEXT,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text-subtlest': Tokens.COLOR_TEXT_SUBTLEST,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-text-disabled': Tokens.COLOR_TEXT_DISABLED,
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const RightIconWrapper = styled.div({
	marginLeft: token('space.025', '2px'),
	marginRight: token('space.025', '2px'),
});

export default SimpleSearchField;
